/**
 * Approver Configuration Modal - Supports all 8 approver types per stage
 */

import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  User, 
  Users, 
  UserCheck, 
  Shield, 
  Building2, 
  GitBranch, 
  Settings, 
  Search,
  Plus,
  Trash2,
  ChevronRight,
  UserCog,
  Crown,
  MapPin,
  Layers,
  AlertCircle,
} from "lucide-react";

// Approver types as per documentation
const APPROVER_TYPES = [
  { 
    value: "user", 
    label: "Specific User", 
    description: "Select individual users by name or email",
    icon: User,
    category: "Direct"
  },
  { 
    value: "role", 
    label: "System Role", 
    description: "Users with specific system roles (admin, manager, etc.)",
    icon: Shield,
    category: "Direct"
  },
  { 
    value: "approval_role", 
    label: "Approval Role", 
    description: "Custom approval-specific roles (L1, L2, etc.)",
    icon: UserCheck,
    category: "Direct"
  },
  { 
    value: "group", 
    label: "User Group", 
    description: "All members of a specific group",
    icon: Users,
    category: "Direct"
  },
  { 
    value: "group_role", 
    label: "Group Role", 
    description: "Users with specific roles within a group",
    icon: UserCog,
    category: "Group-based"
  },
  { 
    value: "hierarchy_level", 
    label: "Hierarchy Level", 
    description: "Users at specific levels in organizational hierarchy",
    icon: GitBranch,
    category: "Dynamic"
  },
  { 
    value: "dynamic_field", 
    label: "Dynamic Field", 
    description: "Users referenced in request fields (host, manager, etc.)",
    icon: Settings,
    category: "Dynamic"
  },
  { 
    value: "manager_chain", 
    label: "Manager Chain", 
    description: "Reporting line hierarchy (manager → alternate → next level)",
    icon: Crown,
    category: "Dynamic"
  },
];

// Dynamic field options (from request form)
const DYNAMIC_FIELD_OPTIONS = [
  { value: "host", label: "Host Person" },
  { value: "manager", label: "Requester's Manager" },
  { value: "alternate_manager", label: "Alternate Manager" },
  { value: "site_manager", label: "Site Manager" },
  { value: "zone_owner", label: "Zone Owner" },
  { value: "department_head", label: "Department Head" },
  { value: "security_officer", label: "Security Officer" },
  { value: "safety_officer", label: "Safety Officer" },
];

// Hierarchy level options
const HIERARCHY_LEVELS = [
  { value: "1", label: "Level 1 - Direct Reports" },
  { value: "2", label: "Level 2 - Second Level" },
  { value: "3", label: "Level 3 - Third Level" },
  { value: "4", label: "Level 4 - Fourth Level" },
  { value: "5", label: "Level 5 - Fifth Level" },
];

// Manager chain options
const MANAGER_CHAIN_OPTIONS = [
  { value: "direct_manager", label: "Direct Manager" },
  { value: "alternate_manager", label: "Alternate Manager" },
  { value: "second_level", label: "Second Level Manager" },
  { value: "third_level", label: "Third Level Manager" },
  { value: "department_head", label: "Department Head" },
];

interface ApproverConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (approver: any) => void;
  stageId?: number;
  editingApprover?: any;
}

export function ApproverConfigModal({ 
  isOpen, 
  onClose, 
  onSave, 
  stageId,
  editingApprover 
}: ApproverConfigModalProps) {
  const editingConfig = editingApprover?.approverConfig || {};
  const [approverType, setApproverType] = useState(editingApprover?.approverType || "user");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [weight, setWeight] = useState(editingConfig?.weight || 1);
  const [isRequired, setIsRequired] = useState(editingConfig?.required ?? true);
  const [dynamicField, setDynamicField] = useState(editingApprover?.approverReference || "");
  const [hierarchyLevel, setHierarchyLevel] = useState(editingConfig?.levels ? String(editingConfig.levels) : "");
  const [managerChainLevel, setManagerChainLevel] = useState(editingApprover?.approverReference || "");
  const [groupId, setGroupId] = useState(
    editingApprover?.approverType === "group" || editingApprover?.approverType === "group_role"
      ? editingApprover?.approverReference || ""
      : ""
  );
  const [roleId, setRoleId] = useState(editingApprover?.approverReference || "");
  const [groupRoleId, setGroupRoleId] = useState(editingConfig?.roleInGroup || "");

  // Queries for dropdown data
  const { data: users } = trpc.users.list.useQuery({
    search: searchQuery,
    limit: 50
  });
  
  const { data: roles } = trpc.roles.list.useQuery();
  const { data: groups } = trpc.groups.list.useQuery();
  const { data: approvalRoles } = trpc.workflows.listApprovalRoles.useQuery();

  // Group approver types by category
  const groupedApproverTypes = useMemo(() => {
    const groups: Record<string, typeof APPROVER_TYPES> = {};
    APPROVER_TYPES.forEach(at => {
      if (!groups[at.category]) {
        groups[at.category] = [];
      }
      groups[at.category].push(at);
    });
    return groups;
  }, []);

  const selectedApproverType = APPROVER_TYPES.find(at => at.value === approverType);

  const handleSave = () => {
    if (!approverType) {
      toast.error("Please select an approver type");
      return;
    }

    const baseConfig: Record<string, any> = {
      weight,
      required: isRequired,
    };
    let payload: any[] = [];

    // Build configuration based on type
    switch (approverType) {
      case "user":
        if (selectedItems.length === 0) {
          toast.error("Please select at least one user");
          return;
        }
        payload = selectedItems.map((user, index) => ({
          approverType: "user",
          approverReference: String(user.id),
          approverConfig: { ...baseConfig },
          priority: index,
          isBackup: false,
        }));
        break;
        
      case "role":
        if (!roleId) {
          toast.error("Please select a role");
          return;
        }
        payload = [{
          approverType: "role",
          approverReference: roleId,
          approverConfig: { ...baseConfig },
          priority: 0,
          isBackup: false,
        }];
        break;
        
      case "approval_role":
        if (!roleId) {
          toast.error("Please select an approval role");
          return;
        }
        payload = [{
          approverType: "approval_role",
          approverReference: roleId,
          approverConfig: { ...baseConfig },
          priority: 0,
          isBackup: false,
        }];
        break;
        
      case "group":
        if (!groupId) {
          toast.error("Please select a group");
          return;
        }
        payload = [{
          approverType: "group",
          approverReference: groupId,
          approverConfig: { ...baseConfig },
          priority: 0,
          isBackup: false,
        }];
        break;
        
      case "group_role":
        if (!groupId || !groupRoleId) {
          toast.error("Please select both group and role");
          return;
        }
        payload = [{
          approverType: "group_role",
          approverReference: groupId,
          approverConfig: {
            ...baseConfig,
            roleInGroup: groupRoleId,
          },
          priority: 0,
          isBackup: false,
        }];
        break;
        
      case "hierarchy_level":
        if (!hierarchyLevel) {
          toast.error("Please select a hierarchy level");
          return;
        }
        payload = [{
          approverType: "hierarchy_level",
          approverConfig: {
            ...baseConfig,
            levels: parseInt(hierarchyLevel, 10),
          },
          priority: 0,
          isBackup: false,
        }];
        break;
        
      case "dynamic_field":
        if (!dynamicField) {
          toast.error("Please select a dynamic field");
          return;
        }
        payload = [{
          approverType: "dynamic_field",
          approverReference: dynamicField,
          approverConfig: { ...baseConfig },
          priority: 0,
          isBackup: false,
        }];
        break;
        
      case "manager_chain":
        if (!managerChainLevel) {
          toast.error("Please select a manager chain level");
          return;
        }
        payload = [{
          approverType: "manager_chain",
          approverReference: managerChainLevel,
          approverConfig: { ...baseConfig },
          priority: 0,
          isBackup: false,
        }];
        break;
    }

    onSave({
      id: editingApprover?.id,
      approvers: payload,
    });

    // Reset form
    setApproverType("user");
    setSelectedItems([]);
    setWeight(1);
    setIsRequired(true);
    setDynamicField("");
    setHierarchyLevel("");
    setManagerChainLevel("");
    setGroupId("");
    setRoleId("");
    setGroupRoleId("");
    onClose();
  };

  const handleAddUser = (user: any) => {
    if (selectedItems.find(item => item.id === user.id)) {
      toast.error("User already added");
      return;
    }
    setSelectedItems([...selectedItems, user]);
  };

  const handleRemoveUser = (userId: number) => {
    setSelectedItems(selectedItems.filter(item => item.id !== userId));
  };

  const renderConfigurationForm = () => {
    switch (approverType) {
      case "user":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Search Users</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6B]" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {searchQuery && users?.users && (
              <div className="max-h-40 overflow-y-auto border rounded-md">
                {users.users.length === 0 ? (
                  <div className="p-4 text-center text-[#6B6B6B]">No users found</div>
                ) : (
                  <div className="divide-y">
                    {users.users.map((user: any) => (
                      <div
                        key={user.id}
                        className="p-3 hover:bg-[#F5F5F5] cursor-pointer flex items-center justify-between"
                        onClick={() => handleAddUser(user)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#5B2C93] text-white flex items-center justify-center text-sm font-medium">
                            {user.name?.charAt(0) || user.email?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium">{user.name || user.email}</div>
                            {user.name && user.email && (
                              <div className="text-sm text-[#6B6B6B]">{user.email}</div>
                            )}
                          </div>
                        </div>
                        <Plus className="h-4 w-4 text-[#5B2C93]" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedItems.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Users ({selectedItems.length})</Label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {selectedItems.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-2 border rounded-md">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#5B2C93] text-white flex items-center justify-center text-xs font-medium">
                          {user.name?.charAt(0) || user.email?.charAt(0)}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{user.name || user.email}</div>
                          {user.name && user.email && (
                            <div className="text-xs text-[#6B6B6B]">{user.email}</div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveUser(user.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case "role":
        return (
          <div className="space-y-2">
            <Label>Select System Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a system role..." />
              </SelectTrigger>
              <SelectContent>
                {roles?.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      {role.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "approval_role":
        return (
          <div className="space-y-2">
            <Label>Select Approval Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an approval role..." />
              </SelectTrigger>
              <SelectContent>
                {approvalRoles?.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)}>
                    <div className="flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      {role.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "group":
        return (
          <div className="space-y-2">
            <Label>Select User Group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a group..." />
              </SelectTrigger>
              <SelectContent>
                {groups?.map((group) => (
                  <SelectItem key={group.id} value={String(group.id)}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {group.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "group_role":
        return (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Select Group</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a group..." />
                </SelectTrigger>
                <SelectContent>
                  {groups?.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Role in Group</Label>
              <Select value={groupRoleId} onValueChange={setGroupRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a role..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Group Admin</SelectItem>
                  <SelectItem value="moderator">Moderator</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case "hierarchy_level":
        return (
          <div className="space-y-2">
            <Label>Select Hierarchy Level</Label>
            <Select value={hierarchyLevel} onValueChange={setHierarchyLevel}>
              <SelectTrigger>
                <SelectValue placeholder="Choose hierarchy level..." />
              </SelectTrigger>
              <SelectContent>
                {HIERARCHY_LEVELS.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-4 w-4" />
                      {level.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "dynamic_field":
        return (
          <div className="space-y-2">
            <Label>Select Dynamic Field</Label>
            <Select value={dynamicField} onValueChange={setDynamicField}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a dynamic field..." />
              </SelectTrigger>
              <SelectContent>
                {DYNAMIC_FIELD_OPTIONS.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      {field.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-[#6B6B6B]">
              The approver will be dynamically resolved from the request field value.
            </p>
          </div>
        );

      case "manager_chain":
        return (
          <div className="space-y-2">
            <Label>Select Manager Chain Level</Label>
            <Select value={managerChainLevel} onValueChange={setManagerChainLevel}>
              <SelectTrigger>
                <SelectValue placeholder="Choose manager chain level..." />
              </SelectTrigger>
              <SelectContent>
                {MANAGER_CHAIN_OPTIONS.map((level) => (
                  <SelectItem key={level.value} value={level.value}>
                    <div className="flex items-center gap-2">
                      <Crown className="h-4 w-4" />
                      {level.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-[#6B6B6B]">
              The approver will be resolved from the requester's reporting chain.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingApprover ? "Edit Approver" : "Add Approver"}
          </DialogTitle>
          <DialogDescription>
            Configure who can approve requests at this stage
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Approver Type Selection */}
          <div className="space-y-4">
            <Label>Approver Type *</Label>
            <Tabs value={approverType} onValueChange={setApproverType} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="user">Direct</TabsTrigger>
                <TabsTrigger value="hierarchy_level">Dynamic</TabsTrigger>
              </TabsList>
              
              {Object.entries(groupedApproverTypes).map(([category, types]) => (
                <TabsContent key={category} value={category === "Direct" ? "user" : "hierarchy_level"} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {types.map((type) => {
                      const Icon = type.icon;
                      return (
                        <Card
                          key={type.value}
                          className={`cursor-pointer transition-colors ${
                            approverType === type.value
                              ? "border-primary bg-[#5B2C93]/5"
                              : "hover:bg-[#F5F5F5]/50"
                          }`}
                          onClick={() => setApproverType(type.value)}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start gap-3">
                              <Icon className="h-5 w-5 text-[#5B2C93] mt-0.5" />
                              <div className="flex-1">
                                <h4 className="font-medium text-sm">{type.label}</h4>
                                <p className="text-xs text-[#6B6B6B] mt-1">{type.description}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Configuration Form */}
          {selectedApproverType && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <selectedApproverType.icon className="h-5 w-5" />
                  {selectedApproverType.label} Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderConfigurationForm()}
              </CardContent>
            </Card>
          )}

          {/* Additional Settings */}
          {selectedApproverType && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Additional Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Weight</Label>
                    <Input
                      type="number"
                      min={1}
                      value={weight}
                      onChange={(e) => setWeight(parseInt(e.target.value) || 1)}
                    />
                    <p className="text-xs text-[#6B6B6B]">
                      Used for percentage-based approval modes
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <Switch
                      checked={isRequired}
                      onCheckedChange={setIsRequired}
                    />
                    <Label>Required Approver</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!selectedApproverType}>
            {editingApprover ? "Update Approver" : "Add Approver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
